import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'

// SkillCardListItem — one row in the Habilidades skill list (look-and-feel
// 52-bot-skills.html view 1). Name + intent + status pill (text + dot, not
// colour-only) + steps/meta + executions, opening the FlowEditor on click.

export type SkillStatus = 'active' | 'draft' | 'paused'

const STATUS_STYLE: Record<SkillStatus, string> = {
  active: 'bg-ok-bg text-ok',
  draft: 'bg-field text-text-2',
  paused: 'bg-warn-bg text-warn',
}

function DotsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className="size-3.5">
      <circle cx="12" cy="5" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="12" cy="19" r="1.6" />
    </svg>
  )
}

export interface SkillCardListItemProps {
  name: string
  /** La clave del disparador. Opcional: hay listas (los flujos de un agente en
   *  /asistente) donde el disparador no viaja en la respuesta, y "intent: " con
   *  nada detrás sería una etiqueta vacía en pantalla. */
  intent?: string
  status: SkillStatus
  steps: number
  /** e.g. "editada hoy por Sofía R." */
  meta?: string
  executions?: number
  icon?: ReactNode
  iconTone?: 'cal' | 'cancel' | 'tag' | 'neutral'
  /** Opens the skill (FlowEditor). */
  onClick?: () => void
  onMenu?: () => void
  className?: string
}

const ICON_TONE: Record<NonNullable<SkillCardListItemProps['iconTone']>, string> = {
  cal: 'bg-[color-mix(in_srgb,var(--brand-blue)_10%,transparent)] text-link',
  cancel: 'bg-sens-bg text-sens',
  tag: 'bg-ok-bg text-ok',
  neutral: 'bg-field text-text-2',
}

export function SkillCardListItem({
  name,
  intent,
  status,
  steps,
  meta,
  executions,
  icon,
  iconTone = 'neutral',
  onClick,
  onMenu,
  className,
}: SkillCardListItemProps) {
  const { t } = useTranslation('components')
  const detail = [t('skill.steps', { count: steps }), meta].filter(Boolean).join(' · ')

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-xl border border-line bg-surface px-4 py-3',
        className,
      )}
    >
      {icon && (
        <span
          className={cn(
            'grid size-9 flex-none place-items-center rounded-lg [&_svg]:size-4',
            ICON_TONE[iconTone],
          )}
        >
          {icon}
        </span>
      )}
      <button
        type="button"
        onClick={onClick}
        className="flex min-w-0 flex-1 flex-col gap-0.5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
      >
        <span className="flex items-center gap-2">
          <span className="truncate text-[13px] font-bold text-text">{name}</span>
          {intent !== undefined && (
            <span className="font-mono text-[10px] font-semibold text-accent-ink">
              intent: {intent}
            </span>
          )}
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-[9.5px] font-bold',
              STATUS_STYLE[status],
            )}
          >
            <span aria-hidden className="size-1.5 rounded-full bg-current" />
            {t(`skill.status.${status}`)}
          </span>
        </span>
        <span className="truncate text-[11.5px] text-text-2">{detail}</span>
      </button>
      {executions != null && (
        <span className="flex-none text-[11px] text-text-3">
          <b className="font-bold tabular-nums text-text-2">{executions}</b> {t('skill.executions')}
        </span>
      )}
      {onMenu && (
        <button
          type="button"
          onClick={onMenu}
          aria-label={t('skill.menu')}
          className="grid size-7 flex-none place-items-center rounded-lg text-text-3 transition-colors hover:bg-field hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue"
        >
          <DotsIcon />
        </button>
      )}
    </div>
  )
}
